import { Component } from '@theme/component';

class JanetFactoryComponent extends Component {
  #events;
  #initialized = new WeakSet();

  connectedCallback() {
    super.connectedCallback();
    this.#connectFilms();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#connectFilms();
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.querySelectorAll('video').forEach((video) => video.pause());
    super.disconnectedCallback();
  }

  #connectFilms() {
    this.#events?.abort();
    this.#events = new AbortController();
    const options = { signal: this.#events.signal };
    const videos = [...this.querySelectorAll('video')];
    const main = this.querySelector('[data-factory-main] video');
    const detail = this.querySelector('[data-factory-detail] video');
    const sound = this.querySelector('[data-factory-sound]');
    const toggle = this.querySelector('[data-factory-toggle]');
    const detailPlay = this.querySelector('[data-detail-play]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');

    for (const video of videos) {
      video.addEventListener('play', () => {
        for (const other of videos) {
          if (other !== video && !other.paused) other.pause();
        }
      }, options);
    }

    if (main instanceof HTMLVideoElement && sound instanceof HTMLButtonElement && toggle instanceof HTMLButtonElement) {
      const sync = () => {
        const audible = !main.muted && main.volume > 0;
        sound.setAttribute('aria-pressed', String(audible));
        sound.querySelector('[data-control-label]').textContent = audible ? sound.dataset.labelOff : sound.dataset.labelOn;
        toggle.querySelector('[data-control-label]').textContent = main.paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause;
      };
      sound.hidden = false;
      toggle.hidden = false;
      sync();
      for (const name of ['play', 'pause', 'ended', 'volumechange']) main.addEventListener(name, sync, options);
      sound.addEventListener('click', () => {
        if (main.muted || main.volume === 0) {
          main.muted = false;
          main.volume = .4;
          if (main.paused) main.play().catch(sync);
        } else {
          main.muted = true;
        }
        sync();
      }, options);
      toggle.addEventListener('click', () => {
        if (main.paused) main.play().catch(sync);
        else main.pause();
      }, options);
      let resumeOnVisible = false;
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          resumeOnVisible = !main.paused;
          main.pause();
        } else if (resumeOnVisible && !reduced.matches) {
          resumeOnVisible = false;
          main.play().catch(sync);
        }
      }, options);
      reduced.addEventListener('change', () => { if (reduced.matches) main.pause(); }, options);
      main.addEventListener('error', () => {
        const message = this.querySelector('[data-main-error]');
        if (message instanceof HTMLElement) message.hidden = false;
      }, options);
      if (!this.#initialized.has(main)) {
        this.#initialized.add(main);
        main.muted = true;
        if (this.dataset.autoplay === 'true' && !reduced.matches) {
          main.autoplay = true;
          main.play().catch(sync);
        }
      }
    }

    if (detail instanceof HTMLVideoElement && detailPlay instanceof HTMLButtonElement) {
      detailPlay.hidden = !detail.paused || (detail.currentTime > 0 && !detail.ended);
      detailPlay.addEventListener('click', async () => {
        detailPlay.disabled = true;
        try {
          await detail.play();
        } catch (reason) {
          if (reason?.name === 'AbortError') {
            detailPlay.hidden = detail.currentTime > 0;
          } else {
            detailPlay.hidden = true;
            const message = this.querySelector('[data-detail-error]');
            if (message instanceof HTMLElement) message.hidden = false;
          }
        } finally {
          detailPlay.disabled = false;
        }
      }, options);
      detail.addEventListener('play', () => { detailPlay.hidden = true; }, options);
      detail.addEventListener('ended', () => { detailPlay.hidden = false; }, options);
    }
  }
}

if (!customElements.get('janet-factory-component')) customElements.define('janet-factory-component', JanetFactoryComponent);
